import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { productsService } from '../services/api';
import config from '../config';
import StockDetailsModal from '../components/products/StockDetailsModal';

const formatPrice = (value) =>
  typeof value === 'number' && !Number.isNaN(value)
    ? `${value.toFixed(2)}€`
    : 'N/A';

const normalizeVariants = (product) => {
  if (!product) return [];
  const variants = product.variants || product.product_variants || [];
  return Array.isArray(variants) ? variants : [];
};

const getProductPriceValue = (product, field) => {
  if (!product) return null;
  const alt = field === 'priceTtc' ? 'price_ttc' : 'price_ht';
  const raw = product[field];
  const fallback = product[alt];
  const numeric = Number(raw !== undefined ? raw : fallback);
  return Number.isFinite(numeric) ? numeric : null;
};

const getVariantPriceRange = (product, field) => {
  const variants = normalizeVariants(product);
  if (variants.length < 2) {
    return null;
  }

  const fieldAlt = field === 'priceTtc' ? 'price_ttc' : 'price_ht';
  const prices = variants
    .map((variant) => {
      const raw = variant[field];
      const fallback = variant[fieldAlt];
      const value = raw !== undefined ? raw : fallback;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    })
    .filter((value) => value !== null);

  if (prices.length < 2) {
    return null;
  }

  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  if (min === max) {
    return { single: min };
  }

  return { min, max };
};

const getPriceDisplay = (product, field) => {
  const range = getVariantPriceRange(product, field);
  if (range?.single !== undefined) {
    return formatPrice(range.single);
  }
  if (range) {
    return `${formatPrice(range.min)} - ${formatPrice(range.max)}`;
  }
  const value = getProductPriceValue(product, field);
  return formatPrice(value);
};

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listState = location.state?.fromProductEdit ? location.state : null;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(listState?.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(listState?.rowsPerPage ?? 10);
  const [searchTerm, setSearchTerm] = useState(listState?.searchTerm ?? '');
  const [searchInput, setSearchInput] = useState(listState?.searchTerm ?? '');
  const [pagination, setPagination] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);

  // Stock Modal State
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedProductForStock, setSelectedProductForStock] = useState(null);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, searchTerm]);

  // Garder l'état de la liste dans l'historique pour que "Retour" du navigateur restaure la page
  useEffect(() => {
    navigate('/products', { state: getListState(), replace: true });
  }, [page, rowsPerPage, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Utiliser la recherche si un terme de recherche est fourni
      const serviceCall = searchTerm.trim()
        ? productsService.search(searchTerm, {
          page: page + 1,
          limit: rowsPerPage
        })
        : productsService.getAll({
          page: page + 1,
          limit: rowsPerPage
        });

      const response = await serviceCall;

      // Gérer la nouvelle structure avec pagination
      if (response.data.products && response.data.pagination) {
        setProducts(response.data.products);
        setPagination(response.data.pagination);
        setTotalProducts(response.data.pagination.totalProducts);
      } else if (Array.isArray(response.data)) {
        // Fallback pour l'ancienne structure
        setProducts(response.data);
        setPagination(null);
        setTotalProducts(response.data.length);
      } else {
        console.error('Structure de réponse inattendue:', response.data);
        setProducts([]);
        setPagination(null);
        setTotalProducts(0);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      setProducts([]);
      setPagination(null);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
      setSearchTerm(searchInput.trim());
    }
  };

  const handleSearchBlur = () => {
    // Optionnel : mettre à jour la recherche quand on quitte le champ
  };

  const getListState = () => ({
    fromProductEdit: true,
    page,
    rowsPerPage,
    searchTerm,
  });

  const handleEditProduct = (productId) => {
    navigate(`/products/edit/${productId}`, { state: getListState() });
  };

  const handleNewProduct = () => {
    navigate('/products/new', { state: getListState() });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await productsService.delete(id);
        fetchData();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleOpenStockModal = (event, product) => {
    event.stopPropagation();
    if (product.variants && product.variants.length > 0) {
      setSelectedProductForStock(product);
      setStockModalOpen(true);
    }
  };

  // Utiliser les produits directement (la pagination et la recherche sont gérées côté serveur)
  const displayProducts = products;

  const getProductImage = (product) => {
    const baseUrl = config.API_URL?.replace(/\/$/, '');
    const imageCandidate = product.image_url || product.imageUrl || product.thumbnail || product.coverImage || product.cover_url;
    const relationImage = product.images?.length ? product.images[0].url || product.images[0].path || product.images[0].full_path : null;
    const selected = imageCandidate || relationImage;
    if (!selected) return null;
    if (/^https?:\/\//i.test(selected)) {
      return selected;
    }
    if (baseUrl) {
      return `${baseUrl}${selected.startsWith('/') ? '' : '/'}${selected}`;
    }
    return selected;
  };

  if (loading) {
    return <LoadingSpinner message="Chargement des produits..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Gestion des Produits
          </Typography>
          {pagination && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {searchTerm ? `Résultats pour "${searchTerm}": ` : ''}
              {pagination.totalProducts} produit{pagination.totalProducts > 1 ? 's' : ''}
              {pagination.totalPages > 1 && ` • Page ${pagination.currentPage}/${pagination.totalPages}`}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewProduct}
        >
          Nouveau Produit
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher un produit..."
          value={searchInput}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          onBlur={handleSearchBlur}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={72}>Image</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Prix HT</TableCell>
              <TableCell>Prix TTC</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayProducts.map((product) => {
              const imageSrc = getProductImage(product);
              return (
                <TableRow key={product.id}>
                  <TableCell>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {imageSrc ? (
                        <Box
                          component="img"
                          src={imageSrc}
                          alt={product.name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Aucune image
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {product.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {product.description?.substring(0, 50)}...
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{getPriceDisplay(product, 'priceHt')}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {getPriceDisplay(product, 'priceTtc')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {product.variants && product.variants.length > 0 ? (
                      <Chip
                        label={`${product.stock} (Var.)`}
                        color={product.stock < (product.stockMin || 5) ? 'warning' : 'info'}
                        size="small"
                        onClick={(e) => handleOpenStockModal(e, product)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <Chip
                        label={`${product.stock} ${product.stock < (product.stockMin || 5) ? '(Bas)' : ''}`}
                        color={product.stock < (product.stockMin || 5) ? 'error' : 'success'}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.isActive ? 'Actif' : 'Inactif'}
                      color={product.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditProduct(product.id)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(product.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination ? pagination.totalProducts : totalProducts}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Produits par page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />
      </TableContainer>

      <StockDetailsModal
        open={stockModalOpen}
        onClose={() => setStockModalOpen(false)}
        product={selectedProductForStock}
      />
    </Box>
  );
};

export default Products;
